import React, { useMemo } from "react";
import { widgetKind } from "./dashboard-api";
import { isNotable } from "./useWidgetData";

/**
 * Plain-language verdict for the clicked store.
 *
 * Deliberately restates rather than diagnoses: open issues come straight from
 * the entity's alert violations, and readings are the widgets' own titles and
 * values. Nothing here infers a cause or invents a severity, so what it says
 * can be trusted against New Relic.
 */

const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

export default function StoreVerdict({ source, results, widgets }) {
  const openIssues = useMemo(() => {
    const violations = source?.recentAlertViolations || [];
    const open = violations.filter((v) => !v.closedAt);

    // The same condition re-fires as separate violations; collapse by label
    // and keep the earliest opening, which is when the problem actually began.
    const byLabel = new Map();
    for (const v of open) {
      const existing = byLabel.get(v.label);
      if (!existing) {
        byLabel.set(v.label, { ...v, count: 1 });
      } else {
        existing.count += 1;
        if (new Date(v.openedAt) < new Date(existing.openedAt)) {
          existing.openedAt = v.openedAt;
        }
      }
    }
    return [...byLabel.values()].sort(
      (a, b) => new Date(a.openedAt) - new Date(b.openedAt)
    );
  }, [source]);

  const { readings, errored, pending } = useMemo(() => {
    const out = { readings: [], errored: [], answered: 0, pending: 0 };

    for (const widget of widgets || []) {
      const result = results?.[widget.id];
      if (!result) {
        out.pending += 1;
        continue;
      }
      out.answered += 1;

      if (result.error) {
        out.errored.push(widget);
        continue;
      }

      const kind = widgetKind(widget);
      if (!isNotable(kind, result.rows)) continue;

      if (kind === "billboard") {
        const row = result.rows[0] || {};
        const key = Object.keys(row).find((k) => typeof row[k] === "number");
        out.readings.push({ title: widget.title, detail: `${row[key]} ${key}` });
      } else {
        const n = result.rows.length;
        out.readings.push({
          title: widget.title,
          detail: `${n} ${n === 1 ? "row" : "rows"}`,
        });
      }
    }
    return out;
  }, [results, widgets]);

  return (
    <section className="verdict">
      <div className="verdict__head">
        <span
          className={`verdict__badge verdict__badge--${
            openIssues.length ? "bad" : "ok"
          }`}
        >
          {openIssues.length
            ? `${openIssues.length} open ${openIssues.length === 1 ? "issue" : "issues"}`
            : "No open issues"}
        </span>
        {pending > 0 ? (
          <span className="verdict__pending">checking {pending} more…</span>
        ) : null}
      </div>

      {openIssues.length ? (
        <ul className="verdict__issues">
          {openIssues.map((issue) => (
            <li key={issue.label}>
              <span className={`entity-dot entity-dot--${String(issue.level || "").toLowerCase()}`} />
              <span className="verdict__issue-label">
                {issue.label}
                {issue.count > 1 ? ` (×${issue.count})` : ""}
              </span>
              <span className="verdict__issue-time">
                {relativeTime(issue.openedAt) || ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Readings only earn their place next to an issue — the dashboard is
          open below, so with nothing wrong they just restate it. */}
      {openIssues.length && readings.length ? (
        <div className="verdict__readings">
          <h4>Readings</h4>
          <ul>
            {readings.map((r) => (
              <li key={r.title}>
                <span className="verdict__reading-title">{r.title}</span>
                <span className="verdict__reading-value">{r.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {errored.length ? (
        <p className="verdict__errored" role="alert">
          {errored.length} {errored.length === 1 ? "check" : "checks"} failed to
          run — treat the summary above as incomplete.
        </p>
      ) : null}
    </section>
  );
}
